import React, { useEffect, useMemo, useState } from 'react';
import { Button, List, Space, Tag, Typography } from 'antd';
import {
  RightOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  ExceptionOutlined,
  BankOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { EmptyQueueState } from '@/components/sales/control-tower/EmptyQueueState';
import type { FinanceDashboardActionSummary } from '@/services/financeDashboard.service';
import { listVatReturns } from '@/services/vatReturn.service';
import recognitionService from '@/services/financeRecognition.service';

const { Text } = Typography;

type Severity = 'critical' | 'attention' | 'info';

interface AttentionItem {
  id: string;
  severity: Severity;
  title: string;
  reason: string;
  count: number;
  amount?: number;
  action_label: string;
  section?: string;
  link_path?: string;
  owner_hint?: string;
}

const severityColor: Record<Severity, string> = {
  critical: 'red',
  attention: 'orange',
  info: 'blue',
};

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const FinanceAttentionBrief: React.FC<{
  summary: FinanceDashboardActionSummary;
  formatCurrency: (v: number) => string;
}> = ({ summary, formatCurrency }) => {
  const { t } = useTranslation('finance');
  const navigate = useNavigate();
  const [extraItems, setExtraItems] = useState<AttentionItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const extras: AttentionItem[] = [];
      try {
        const ws = await recognitionService.workspace();
        const overdue = Number(ws?.kpis?.overdue_count ?? 0);
        const blocked = Number(ws?.kpis?.blocked_count ?? 0);
        if (overdue > 0 || blocked > 0) {
          extras.push({
            id: 'recognition',
            severity: overdue > 0 ? 'critical' : 'attention',
            title: t('controlTower.recognitionExceptions'),
            reason:
              overdue > 0
                ? t('controlTower.recognitionOverdueReason', { count: overdue })
                : t('controlTower.recognitionBlockedReason', { count: blocked }),
            count: overdue || blocked,
            action_label: t('controlTower.openWorkspace'),
            link_path: '/fin/prepaid',
            owner_hint: t('controlTower.ownerController'),
          });
        }
      } catch {
        /* soft-fail */
      }
      try {
        const returns = await listVatReturns();
        const open = (Array.isArray(returns) ? returns : []).filter((r: any) =>
          ['Draft', 'Calculated', 'Validated', 'Open'].includes(String(r?.status ?? ''))
        );
        if (open.length > 0) {
          extras.push({
            id: 'vat',
            severity: 'info',
            title: t('controlTower.vatReturnsInProgress'),
            reason: t('controlTower.vatReturnsReason'),
            count: open.length,
            action_label: t('controlTower.vatReturns'),
            link_path: '/fin/tax/vat-return',
            owner_hint: t('controlTower.ownerTax'),
          });
        }
      } catch {
        /* soft-fail */
      }
      if (!cancelled) setExtraItems(extras);
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const items = useMemo(() => {
    const out: AttentionItem[] = [];
    const arOverdue = summary.overdue_customer_invoice_count ?? 0;
    const apOverdue = summary.overdue_supplier_invoice_count ?? 0;
    const ar90 = summary.overdue_customer_aging?.['90+']?.count ?? 0;
    const ap90 = summary.overdue_supplier_aging?.['90+']?.count ?? 0;
    const dueTodayAr = summary.due_today_customer_invoice_count ?? 0;
    const dueTodayAp = summary.due_today_supplier_invoice_count ?? 0;
    const recoveryOverdue = summary.supplier_advance_recovery?.overdue_count ?? 0;
    const approvals =
      (summary.pending_approval_pv_count ?? 0) +
      (summary.pending_approval_jv_count ?? 0) +
      (summary.pending_approval_grn_count ?? 0);
    const monthEndBlockers =
      (summary.month_end?.draft_receipts ?? 0) +
      (summary.month_end?.draft_payments ?? 0) +
      (summary.month_end?.unposted_journals ?? 0);

    if (arOverdue > 0) {
      out.push({
        id: 'overdue-ar',
        severity: 'critical',
        title: t('controlTower.overdueCustomerInvoices'),
        reason:
          ar90 > 0
            ? t('controlTower.overdueAr90Reason', { count: ar90 })
            : t('controlTower.overdueArReason'),
        count: arOverdue,
        amount: summary.overdue_customer_invoice_value,
        action_label: t('controlTower.collectionsQueue'),
        section: 'collections-payables',
        owner_hint: t('controlTower.ownerCollections'),
      });
    }
    if (apOverdue > 0) {
      out.push({
        id: 'overdue-ap',
        severity: 'critical',
        title: t('controlTower.overdueSupplierBills'),
        reason:
          ap90 > 0
            ? t('controlTower.overdueAp90Reason', { count: ap90 })
            : t('controlTower.overdueApReason'),
        count: apOverdue,
        amount: summary.overdue_supplier_invoice_value,
        action_label: t('controlTower.payablesQueue'),
        section: 'collections-payables',
        owner_hint: t('controlTower.ownerAp'),
      });
    }
    if (dueTodayAr + dueTodayAp > 0) {
      out.push({
        id: 'due-today',
        severity: 'attention',
        title: t('controlTower.dueTodayArAp'),
        reason: t('controlTower.dueTodayReason'),
        count: dueTodayAr + dueTodayAp,
        amount:
          (summary.due_today_customer_invoice_value ?? 0) +
          (summary.due_today_supplier_invoice_value ?? 0),
        action_label: t('controlTower.review'),
        section: 'collections-payables',
        owner_hint: t('controlTower.ownerCash'),
      });
    }
    if (recoveryOverdue > 0) {
      out.push({
        id: 'advance-recovery',
        severity: 'attention',
        title: t('controlTower.supplierAdvanceRecoveryOverdue'),
        reason: t('controlTower.supplierAdvanceRecoveryReason'),
        count: recoveryOverdue,
        amount: summary.supplier_advance_recovery?.exposure_total,
        action_label: t('controlTower.recovery'),
        section: 'document-preparation',
        owner_hint: t('controlTower.ownerAp'),
      });
    }
    if (approvals > 0) {
      out.push({
        id: 'approvals',
        severity: 'attention',
        title: t('controlTower.pendingFinanceApprovals'),
        reason: t('controlTower.pendingFinanceApprovalsReason'),
        count: approvals,
        action_label: t('controlTower.clearQueue'),
        section: 'pending-approvals',
        owner_hint: t('controlTower.ownerApprovers'),
      });
    }
    if ((summary.supplier_advance_exposure ?? 0) > 0 && (summary.advances_awaiting_adjustment_count ?? 0) > 0) {
      out.push({
        id: 'advance-exposure',
        severity: 'attention',
        title: t('controlTower.agedSupplierAdvances'),
        reason: t('controlTower.agedSupplierAdvancesReason'),
        count: summary.advances_awaiting_adjustment_count ?? 0,
        amount: summary.supplier_advance_exposure,
        action_label: t('controlTower.advances'),
        section: 'document-preparation',
        owner_hint: t('controlTower.ownerAp'),
      });
    }
    if (monthEndBlockers > 0) {
      out.push({
        id: 'month-end',
        severity: 'info',
        title: t('controlTower.monthEndReadinessBlockers'),
        reason: t('controlTower.monthEndBlockersReason'),
        count: monthEndBlockers,
        action_label: t('controlTower.checklist'),
        section: 'month-end',
        owner_hint: t('controlTower.ownerController'),
      });
    }

    return [...out, ...extraItems];
  }, [summary, extraItems, t]);

  const severityLabel = (s: Severity) => {
    if (s === 'critical') return t('controlTower.severityCritical');
    if (s === 'attention') return t('controlTower.severityAttention');
    return t('controlTower.severityInfo');
  };

  const highlights = useMemo(() => {
    const tags: string[] = [];
    if ((summary.overdue_customer_invoice_count ?? 0) > 0) {
      tags.push(t('controlTower.highlightOverdueAr', { count: summary.overdue_customer_invoice_count }));
    }
    if ((summary.overdue_supplier_invoice_count ?? 0) > 0) {
      tags.push(t('controlTower.highlightOverdueAp', { count: summary.overdue_supplier_invoice_count }));
    }
    if ((summary.overdue_customer_aging?.['90+']?.count ?? 0) > 0) {
      tags.push(t('controlTower.highlightAr90', { count: summary.overdue_customer_aging['90+'].count }));
    }
    if ((summary.cash_and_bank_balance ?? 0) !== 0) {
      tags.push(t('controlTower.highlightCash', { amount: formatCurrency(summary.cash_and_bank_balance) }));
    }
    return tags;
  }, [summary, formatCurrency, t]);

  const iconFor = (id: string) => {
    if (id.startsWith('overdue')) return <WarningOutlined />;
    if (id === 'due-today') return <ClockCircleOutlined />;
    if (id === 'approvals') return <ExceptionOutlined />;
    if (id === 'vat' || id === 'recognition') return <FileTextOutlined />;
    if (id === 'month-end') return <BankOutlined />;
    return <ThunderboltOutlined />;
  };

  return (
    <ControlTowerSectionCard
      zone="attention"
      sectionId="attention-brief"
      title={
        <Space>
          <ThunderboltOutlined />
          {t('controlTower.attentionBrief')}
        </Space>
      }
    >
      {highlights.length > 0 && (
        <Space wrap style={{ marginBottom: 12 }}>
          {highlights.map((h) => (
            <Tag key={h} color="geekblue">
              {h}
            </Tag>
          ))}
        </Space>
      )}
      {!items.length ? (
        <EmptyQueueState message={t('controlTower.emptyAttention')} />
      ) : (
        <List
          size="small"
          dataSource={items}
          renderItem={(a) => (
            <List.Item
              actions={[
                <Button
                  key="go"
                  type="link"
                  size="small"
                  icon={<RightOutlined />}
                  onClick={() => {
                    if (a.section) scrollTo(a.section);
                    else if (a.link_path) navigate(a.link_path);
                  }}
                >
                  {a.action_label}
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={iconFor(a.id)}
                title={
                  <Space wrap>
                    <Tag color={severityColor[a.severity]}>{severityLabel(a.severity)}</Tag>
                    <Text strong>{a.title}</Text>
                    <Tag>{a.count}</Tag>
                    {a.amount != null ? (
                      <Text type="secondary">{formatCurrency(a.amount)}</Text>
                    ) : null}
                  </Space>
                }
                description={
                  <Text type="secondary">
                    {t('controlTower.whyPrefix', { reason: a.reason })}
                    {a.owner_hint ? t('controlTower.ownerSuffix', { owner: a.owner_hint }) : ''}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </ControlTowerSectionCard>
  );
};

export default FinanceAttentionBrief;
